"use client";

import { useRef } from "react";
import { useWatchingEyes, type EyeRig } from "./useWatchingEyes";

// Geometry copied from TASMA_Wordmark_*.svg (viewBox 0 0 412 100). Do not redraw the letters.
const VIEW_W = 412;
const VIEW_H = 100;
const SOCKET_R = 15;
const PUPIL_R = 7;
const RIG: EyeRig = {
  viewWidth: VIEW_W,
  sockets: [
    { cx: 105, cy: 46 },
    { cx: 369, cy: 46 },
  ],
  rest: { x: 6, y: -4 }, // the logo's resting pupils look up and to the right
  reach: 7.5, // keeps a sliver of socket around the pupil
};

// The approved colourways from the brand guidelines (02 · Logo, colour versions).
const TONES = {
  "offwhite-amber": { letters: "var(--color-offwhite)", pupils: "var(--color-amber)" }, // on Midnight
  "midnight-amber": { letters: "var(--color-midnight)", pupils: "var(--color-amber)" }, // on Off-white
  offwhite: { letters: "var(--color-offwhite)", pupils: "var(--color-offwhite)" }, // on Deep Teal
  teal: { letters: "var(--color-teal)", pupils: "var(--color-teal)" }, // on Off-white
} as const;

type Props = {
  tone?: keyof typeof TONES;
  watching?: boolean;
  className?: string;
};

// The TASMA wordmark. With `watching`, the pupils follow the pointer (or glance around on touch screens) and blink.
export function TasmaWordmark({ tone = "midnight-amber", watching = true, className }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const colors = TONES[tone];
  useWatchingEyes(svgRef, RIG, watching);

  return (
    <svg ref={svgRef} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label="TASMA" className={className}>
      <path fill={colors.letters} d="M0 0 H76 V28 H53 V100 H23 V28 H0 Z" />
      <path
        fillRule="evenodd"
        fill={colors.letters}
        d="M62 100 L88 0 H122 L148 100 H120 L105 74 L90 100 Z M90 46 a15 15 0 1 0 30 0 a15 15 0 1 0 -30 0 Z"
      />
      <path
        fill="none"
        stroke={colors.letters}
        strokeWidth="30"
        d="M221 15 H186.5 A17.5 17.5 0 0 0 186.5 50 H187.5 A17.5 17.5 0 0 1 187.5 85 H151"
      />
      <path fill={colors.letters} d="M225 100 V0 H255 L273 44 L291 0 H321 V100 H293 V52 L281 80 H265 L253 52 V100 Z" />
      <path
        fillRule="evenodd"
        fill={colors.letters}
        d="M326 100 L352 0 H386 L412 100 H384 L369 74 L354 100 Z M354 46 a15 15 0 1 0 30 0 a15 15 0 1 0 -30 0 Z"
      />
      {RIG.sockets.map((s) => (
        <g key={s.cx}>
          <circle data-pupil cx={s.cx + RIG.rest.x} cy={s.cy + RIG.rest.y} r={PUPIL_R} fill={colors.pupils} />
          <circle data-lid cx={s.cx} cy={s.cy} r={SOCKET_R + 0.5} fill={colors.letters} style={{ visibility: "hidden" }} />
        </g>
      ))}
    </svg>
  );
}
